// Turns the store into one request: constitution + what it remembers + the recent conversation.
import type { ChatMessage } from '@/llm/types';
import type { Curiosity } from '@/memory/curiosity';
import type { Retrieved } from '@/memory/retrieve';
import type { JournalEntry, Memory, Message } from '@/types';
import { constitution, OUTPUT_FORMAT, type Identity } from './persona';

const KIND_LABEL: Record<Memory['kind'], string> = {
  about_me: '关于她',
  person: '人',
  event: '事',
  feeling: '感受',
  preference: '偏好',
  belief: '看法',
  prediction: '她的预判',
  followup: '待跟进',
};

const WEEKDAY = ['日', '一', '二', '三', '四', '五', '六'];

export function dateStamp(at: number): string {
  const d = new Date(at);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export function nowLine(at: number): string {
  const d = new Date(at);
  return `现在是 ${dateStamp(at)} 星期${WEEKDAY[d.getDay()]} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}。`;
}

function memoryLine(m: Memory): string {
  const when = m.happenedAt ? `${m.happenedAt}，` : '';
  const state = m.kind === 'prediction' ? (m.status === 'done' ? '（已应验）' : '（还没见分晓）') : '';
  return `- [${KIND_LABEL[m.kind]}] ${when}${m.text}${state}`;
}

export interface AssembleInput {
  identity: Identity;
  retrieved: Retrieved;
  journal: JournalEntry[];
  now: number;
  /** What it may get curious about this turn, and whether it should hold back instead. */
  curiosity?: Curiosity;
  /** The one-time offer to hear about her life in one go; included on exactly one turn. */
  invite?: boolean;
}

/** Pure; exported for tests. */
export function buildSystemPrompt({ identity, retrieved, journal, now, curiosity, invite }: AssembleInput): string {
  const parts: string[] = [constitution(identity)];
  const met = identity.firstMetAt ? `你们 ${dateStamp(identity.firstMetAt)} 认识的。` : '';
  parts.push(`${nowLine(now)}${met}`);

  if (retrieved.profile.length) parts.push(`你知道的关于她的事：\n${retrieved.profile.map(memoryLine).join('\n')}`);
  if (retrieved.relevant.length) parts.push(`和她刚说的有关的记忆：\n${retrieved.relevant.map(memoryLine).join('\n')}`);
  if (retrieved.followups.length) parts.push(`你记着要找机会提的（挑时候，不是现在必须说）：\n${retrieved.followups.map((m) => `- ${m.text}`).join('\n')}`);
  const recent = journal.slice(-7);
  if (recent.length) parts.push(`最近几天：\n${recent.map((j) => `- ${j.date}：${j.text}`).join('\n')}`);
  if (!retrieved.profile.length && !recent.length) parts.push('你们才刚认识，你还不了解她。多听，少猜，不要假装知道。');

  if (curiosity) {
    const who = curiosity.mentions.map((m) => (m.known ? `${m.name}（你知道：${m.known.text}）` : `${m.name}（你还不认识）`));
    if (who.length) parts.push(`她这句话里提到的人：${who.join('；')}。不认识的，顺口问问是谁、什么关系；认识的，可以问点细节或近况。`);
    if (curiosity.hold === 'brake') parts.push('她刚才回得很短，或者说了不想聊：这一轮别追问，也别另起话题问她的事，接住就好。');
    else if (curiosity.hold === 'budget') parts.push('今天别再另起话题问她本人的情况了；顺着她正在说的事追问细节照常。');
    else if (curiosity.gaps.length) parts.push(`你还不太知道的：${curiosity.gaps.map((g) => g.label).join('；')}。这轮结尾的那个问题，优先从和她正在说的事挨着的这些里挑一个具体的来问；一次只问一个，问完就接着聊，别像填表。她在倒情绪就不问。`);
  }
  if (invite) parts.push('找个自然的时候说一次（这轮就说）：她要是愿意，可以一口气跟你说说她生活里的人和事，你记性好；不想说也没关系，慢慢来。说完这次以后不再提。');

  parts.push(OUTPUT_FORMAT);
  return parts.join('\n\n');
}

const GAP_MARK_MS = 30 * 60 * 1000;

function stamp(at: number): string {
  const d = new Date(at);
  return `[${d.getMonth() + 1}/${d.getDate()} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}]`;
}

/**
 * Pure; exported for tests. The recent conversation as alternating provider messages, newest last, within a
 * character budget. Consecutive same-role messages are merged (two openers in a row happen), the sequence is
 * made to start with a user turn (Claude and Gemini require it), and a time stamp is prefixed wherever more
 * than half an hour passed so it can feel the gaps.
 */
export function toChatMessages(messages: Message[], budgetChars = 12000): ChatMessage[] {
  const kept: Message[] = [];
  let used = 0;
  for (let i = messages.length - 1; i >= 0; i--) {
    const m = messages[i];
    if (m.error || !m.text.trim()) continue;
    used += m.text.length;
    if (used > budgetChars && kept.length > 0) break;
    kept.unshift(m);
  }
  const out: ChatMessage[] = [];
  let prevAt = 0;
  for (const m of kept) {
    const gap = prevAt === 0 || m.at - prevAt > GAP_MARK_MS;
    const text = gap && m.role === 'user' ? `${stamp(m.at)} ${m.text}` : m.text;
    prevAt = m.at;
    const last = out[out.length - 1];
    if (last && last.role === m.role) {
      last.content = [{ type: 'text', text: `${(last.content[0] as { text: string }).text}\n\n${text}` }];
    } else {
      out.push({ role: m.role, content: [{ type: 'text', text }] });
    }
  }
  if (out.length && out[0].role === 'assistant') out.unshift({ role: 'user', content: [{ type: 'text', text: '（她打开了对话）' }] });
  return out;
}

/** The turn that makes it speak first, when she opens the app after a while or for the very first time. */
export function openerInstruction(kind: 'first' | 'return', hoursAway: number, identity: Identity, gap?: string): string {
  if (kind === 'first') return `（这是你们第一次见面。她刚给你起了名字「${identity.companionName}」，她叫${identity.userName}。先打个招呼，短一点，别自我介绍一大堆，问问她今天怎么样。）`;
  const away = hoursAway >= 48 ? `${Math.round(hoursAway / 24)} 天` : `${Math.round(hoursAway)} 小时`;
  const curious = gap ? `没有的话，可以顺口问一件小事，比如关于${gap}——像朋友随口问，不像做调查。` : '没有就随便说点什么。';
  return `（她刚打开了对话，距离上次聊已经 ${away}。你先开口，一两条就好。有上次没聊完的事就接着问。${curious}别问「有什么可以帮你」。）`;
}
